import type { Programme } from "@/types";

import { assessmentWeightLedger, isBalanced } from "./assessment-weight";
import { resolveNarrativeTemplate } from "./narrative-templates";

export type StepCompletion = "not-started" | "partial" | "complete";

/** Whether an effort-hours value holds a positive number at any depth. */
export function hasPositiveHours(value: unknown): boolean {
  if (typeof value === "number") {
    return value > 0;
  }
  if (!value || typeof value !== "object") {
    return false;
  }
  return Object.values(value).some(hasPositiveHours);
}

/**
 * Maps a "how many of the total are done" count to a {@link StepCompletion}:
 * none done is `"not-started"`, all done is `"complete"`, anything in
 * between is `"partial"`.
 */
function completionFromCount(count: number, total: number): StepCompletion {
  if (count === 0) {
    return "not-started";
  }
  if (count === total) {
    return "complete";
  }
  return "partial";
}

export function getStepCompletion(stepKey: string, programme: Programme): StepCompletion {
  const modules = programme.modules ?? [];
  switch (stepKey) {
    case "award-pathway": {
      const awards = programme.embeddedAwards ?? [];
      if (awards.length === 0) {
        return "not-started";
      }
      const complete = awards.every(
        (award) =>
          Boolean((award.title ?? "").trim()) &&
          Boolean((award.awardType ?? "").trim()) &&
          award.nfqLevel != null &&
          Number(award.credits) > 0 &&
          ((award.stageIds ?? []).length > 0 || (award.moduleIds ?? []).length > 0),
      );
      return complete ? "complete" : "partial";
    }
    case "outcomes": {
      const completed = (programme.plos ?? []).filter((plo) => Boolean(plo.text?.trim())).length;
      return completionFromCount(completed, (programme.plos ?? []).length);
    }
    case "versions":
      return (programme.versions ?? []).length > 0 ? "complete" : "not-started";
    case "structure":
      return modules.length > 0 ? "complete" : "not-started";
    case "narrative": {
      const fields = resolveNarrativeTemplate(programme).fields;
      const completed = fields.filter(({ field }) => {
        const value = programme.heDescriptor?.[field];
        return typeof value === "string" && Boolean(value.trim());
      }).length;
      return completionFromCount(completed, fields.length);
    }
    case "cas-picker": {
      const casModules = modules.filter((module) => module.casSource === true);
      if (!(programme.casMajorAwardCode ?? "").trim() || casModules.length === 0) {
        return "not-started";
      }
      const credits = casModules.reduce((total, module) => total + Number(module.credits ?? 0), 0);
      return credits === Number(programme.totalCredits ?? 0) ? "complete" : "partial";
    }
    case "stages":
      return (programme.versions ?? []).some((version) => (version.stages ?? []).length > 0)
        ? "complete"
        : "not-started";
    case "mimlos": {
      const count = modules.filter((module) => (module.mimlos ?? []).length > 0).length;
      return completionFromCount(count, modules.length);
    }
    case "effort-hours": {
      if (modules.length === 0) {
        return "not-started";
      }
      const count = modules.filter((module) => hasPositiveHours(module.effortHours)).length;
      return completionFromCount(count, modules.length);
    }
    case "assessments": {
      const ledger = assessmentWeightLedger(programme);
      const count = modules.filter((module) => {
        const entries = ledger.entriesFor(module.id);
        return (
          entries.length > 0 &&
          entries.every(
            (entry) =>
              (entry.assessments.length > 0 || entry.incoming.length > 0) &&
              isBalanced(entry.total),
          )
        );
      }).length;
      return completionFromCount(count, modules.length);
    }
    case "reading-lists": {
      const count = modules.filter((module) => (module.readingList ?? []).length > 0).length;
      return completionFromCount(count, modules.length);
    }
    case "mapping": {
      const plos = programme.plos ?? [];
      const count = plos.filter((plo) => (programme.ploToMimlos?.[plo.id] ?? []).length > 0).length;
      return completionFromCount(count, plos.length);
    }
    default:
      return "complete";
  }
}
